import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { AppInput, AppPhoneInput, AppCheckbox, DeleteIcon } from '@pro-sto/ui';
import { partnersStore } from '@/stores/PartnersStore';
import './PartnerModal.css';

interface AdminFormItem {
  phone: string;
  uuid?: string;
}

const PartnerModal: React.FC = observer(() => {
  const isEditing = !!partnersStore.editingPartner;

  const [name, setName] = useState(partnersStore.editingPartner?.name || '');
  const [address, setAddress] = useState(partnersStore.editingPartner?.address || '');
  const [lat, setLat] = useState(String(partnersStore.editingPartner?.location?.lat ?? ''));
  const [lon, setLon] = useState(String(partnersStore.editingPartner?.location?.lon ?? ''));
  // TODO: posts count — not yet implemented on server; kept for API payload
  const postsCount = partnersStore.editingPartner?.postsCount ?? 1;
  const [isPublished, setIsPublished] = useState(Boolean(partnersStore.editingPartner?.isPublished ?? 0));
  const [admins, setAdmins] = useState<AdminFormItem[]>([]);

  // Sync admins from store (loaded async for editing)
  useEffect(() => {
    if (partnersStore.editingAdmins.length > 0) {
      setAdmins(
        partnersStore.editingAdmins.map((a) => ({
          phone: a.phone,
          uuid: a.uuid,
        }))
      );
    }
  }, [partnersStore.editingAdmins]);

  const handleSubmit = useCallback(() => {
    if (!name.trim() || !address.trim()) return;

    const data = {
      name: name.trim(),
      address: address.trim(),
      lat,
      lon,
      postsCount,
      isPublished: isPublished ? 1 : 0,
      admins: admins
        .filter((a) => a.phone.trim())
        .map((a) => ({
          ...a,
          phone: a.uuid ? a.phone : (a.phone.length === 10 ? `+7${a.phone}` : a.phone),
        })),
    };

    if (isEditing && partnersStore.editingPartner) {
      partnersStore.updateServiceCenter(partnersStore.editingPartner.uuid, data);
    } else {
      partnersStore.createServiceCenter(data);
    }
  }, [name, address, lat, lon, postsCount, isPublished, admins, isEditing]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      partnersStore.closeModal();
    }
  }, []);

  const addAdmin = () => {
    setAdmins((prev) => [...prev, { phone: '' }]);
  };

  const updateAdminPhone = (index: number, phone: string) => {
    setAdmins((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], phone };
      return next;
    });
  };

  const removeAdmin = (index: number) => {
    const admin = admins[index];
    if (admin.uuid && partnersStore.editingPartner) {
      partnersStore.removeAdmin(partnersStore.editingPartner.uuid, admin.uuid);
    }
    setAdmins((prev) => prev.filter((_, i) => i !== index));
  };

  // TODO: posts count — not yet implemented on server
  // const decrementPosts = () => {
  //   setPostsCount((prev) => Math.max(1, prev - 1));
  // };

  // const incrementPosts = () => {
  //   setPostsCount((prev) => prev + 1);
  // };

  const isValid = name.trim().length > 0 && address.trim().length > 0;

  return (
    <div className="partner-modal__overlay" onClick={handleOverlayClick}>
      <div className="partner-modal">

        {/* Static header */}
        <div className="partner-modal__header">
          <h2 className="partner-modal__title">Сервисный центр</h2>
          <button
            className="partner-modal__close"
            onClick={partnersStore.closeModal}
            type="button"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div className="partner-modal__body">
          <div className="partner-modal__section-title">Основное</div>

          <div className="partner-modal__field">
            <AppInput
              label="Название"
              value={name}
              onChange={(val) => setName(val)}
              placeholder="Название сервисного центра"
              background="muted"
            />
          </div>

          <div className="partner-modal__field">
            <AppInput
              label="Адрес"
              value={address}
              onChange={(val) => setAddress(val)}
              placeholder="Адрес сервисного центра"
              background="muted"
            />
          </div>

          <div className="partner-modal__field">
            <span className="partner-modal__field-label">Координаты</span>
            <div className="partner-modal__coords-row">
              <AppInput
                value={lat}
                onChange={(val) => setLat(val)}
                placeholder="Широта"
                background="muted"
              />
              <AppInput
                value={lon}
                onChange={(val) => setLon(val)}
                placeholder="Долгота"
                background="muted"
              />
            </div>
          </div>

          {/* TODO: posts count — not yet implemented on server
          <div className="partner-modal__field">
            <span className="partner-modal__field-label">Кол-во постов</span>
            <div className="partner-modal__posts-control">
              <button
                className="partner-modal__posts-btn"
                onClick={decrementPosts}
                disabled={postsCount <= 1}
                type="button"
                aria-label="Уменьшить"
              >
                −
              </button>
              <input
                className="partner-modal__posts-input"
                type="number"
                value={postsCount}
                min={1}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1) setPostsCount(val);
                }}
              />
              <button
                className="partner-modal__posts-btn"
                onClick={incrementPosts}
                type="button"
                aria-label="Увеличить"
              >
                +
              </button>
            </div>
          </div>
          */}

          <div className="partner-modal__checkbox">
            <AppCheckbox
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              label="Опубликован"
            />
          </div>

          <div className="partner-modal__admins-section">
            <div className="partner-modal__section-title">Администраторы:</div>

            {admins.map((admin, index) => (
              <div className="partner-modal__admin-row" key={admin.uuid || index}>
                <span className="partner-modal__admin-label">
                  Администратор №{index + 1}
                </span>
                <div className="partner-modal__admin-input-row">
                  <div className="partner-modal__admin-phone">
                    {admin.uuid ? (
                      <AppInput
                        value={admin.phone}
                        onChange={() => {}}
                        placeholder="+7 (___) ___-__-__"
                        background="muted"
                        disabled
                      />
                    ) : (
                      <AppPhoneInput
                        onChange={(val) => updateAdminPhone(index, val)}
                        label=""
                        background="muted"
                      />
                    )}
                  </div>
                  <button
                    className="partner-modal__admin-delete"
                    onClick={() => removeAdmin(index)}
                    type="button"
                    aria-label="Удалить администратора"
                  >
                    <DeleteIcon width={18} height={18} />
                  </button>
                </div>
              </div>
            ))}

            <button
              className="partner-modal__add-admin-btn"
              onClick={addAdmin}
              type="button"
            >
              Добавить администратора &nbsp;+
            </button>
          </div>
        </div>

        {/* Static footer */}
        <div className="partner-modal__footer">
          <button
            className="partner-modal__cancel-btn"
            onClick={partnersStore.closeModal}
            type="button"
          >
            Отмена
          </button>
          <button
            className="partner-modal__submit-btn"
            onClick={handleSubmit}
            disabled={!isValid || partnersStore.isSubmitting}
            type="button"
          >
            {partnersStore.isSubmitting
              ? 'Сохранение...'
              : isEditing
                ? 'Сохранить'
                : 'Добавить'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default PartnerModal;
